import Vue from 'vue';
import axios from '@/plugins/axios';
import storeUtils from '@/stores/utils';
import store from '@/stores/store';
import { replaceInvoiceVariables } from '@/utils/invoice-variables';
import {
    TEMPLATE_SOURCE,
    defaultTemplateSource,
    makeVendorForwardInvoiceSubject,
    makeVendorForwardInvoiceMessage,
    vendorForwardI18n,
} from '@/utils/invoice-email';

function translateVendor(key, values) {
    const locale = Vue.prototype.$i18n?.locale || 'fr';
    const dict = vendorForwardI18n[locale] || vendorForwardI18n.en;
    let text = dict[key] || vendorForwardI18n.en[key] || key;
    if (values) {
        Object.keys(values).forEach((k) => {
            text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), values[k] ?? '');
        });
    }
    return text;
}

const state = {
    billingaccount: null,
    billed_email_available: false,
    billed: null,
    biller: null,
    invoice: null,
    subject: null,
    message: null,
    receive_copy: false,
    send_copy_to_billing_emails: false,
    recipients: [],
    sending_invoice: false,
    sending_invoice_errors: {},
    reminder: false,
    template_source: TEMPLATE_SOURCE.BILLING_ACCOUNT,
};

function makeBillingAccountSubject(invoice, billingaccount, reminder = false) {
    let subjectTemplate = null;
    if (invoice.status === 'draft') {
        subjectTemplate = billingaccount?.notification_texts?.draft_invoice_notification_subject;
    } else if (reminder) {
        subjectTemplate = billingaccount?.notification_texts?.invoice_reminder_subject;
    } else {
        subjectTemplate = billingaccount?.notification_texts?.invoice_notification_subject;
    }
    return replaceInvoiceVariables(subjectTemplate, invoice, billingaccount, Vue.prototype.$translate);
}

function makeBillingAccountMessage(invoice, billingaccount, reminder = false) {
    let messageTemplate = null;
    if (invoice.status === 'draft') {
        messageTemplate = billingaccount?.notification_texts?.draft_invoice_notification_text;
    } else if (reminder) {
        messageTemplate = billingaccount?.notification_texts?.invoice_reminder_text;
    } else {
        messageTemplate = billingaccount?.notification_texts?.invoice_notification_text;
    }
    return replaceInvoiceVariables(messageTemplate, invoice, billingaccount, Vue.prototype.$translate);
}

function makeSubject(invoice, billingaccount, reminder, templateSource) {
    if (!invoice) {
        return null;
    }
    if (templateSource === TEMPLATE_SOURCE.VENDOR_FORWARD) {
        return makeVendorForwardInvoiceSubject(invoice, translateVendor);
    }
    return makeBillingAccountSubject(invoice, billingaccount, reminder);
}

function makeMessage(invoice, billingaccount, reminder, templateSource) {
    if (!invoice) {
        return null;
    }
    if (templateSource === TEMPLATE_SOURCE.VENDOR_FORWARD) {
        return makeVendorForwardInvoiceMessage(invoice, translateVendor);
    }
    return makeBillingAccountMessage(invoice, billingaccount, reminder);
}

function agentEntityIdFor(invoice) {
    return (
        invoice?.order_data?.agent ||
        invoice?.order?.agent ||
        store.getters['session/current_entity_id']
    );
}

function applyTemplates(state) {
    if (!state.invoice) {
        state.subject = null;
        state.message = null;
        return;
    }
    state.subject = makeSubject(
        state.invoice,
        state.billingaccount,
        state.reminder,
        state.template_source
    );
    state.message = makeMessage(
        state.invoice,
        state.billingaccount,
        state.reminder,
        state.template_source
    );
}

const getters = {
    ready: (state) => {
        return state.message && state.subject && state.recipients?.length;
    },
};

function invoiceOrderId(invoice) {
    return invoice?.order?.id ?? invoice?.order ?? null;
}

const mutations = {
    updateInvoice(state, invoice) {
        state.invoice = invoice;
        state.billed = invoice?.billed_data;
        state.biller = invoice?.biller_data;
        state.template_source = defaultTemplateSource(
            invoice ? [invoice] : [],
            agentEntityIdFor(invoice)
        );
        applyTemplates(state);
        state.sending_invoice = false;
        state.sending_invoice_errors = {};
    },

    updateSendingInvoice(state, sending) {
        state.sending_invoice = sending;
        state.sending_invoice_errors = {};
    },
    updateSendingInvoiceErrors(state, sending_invoice_errors) {
        state.sending_invoice_errors = sending_invoice_errors;
    },

    updateBillingAccount(state, billingaccount) {
        state.billingaccount = billingaccount;
        applyTemplates(state);
    },

    updateBilled(state, billed) {
        state.billed = billed;
        state.billed_email_available = !!billed?.billing_address?.emails;
    },

    updateMessage(state, message) {
        state.message = message;
    },

    updateSubject(state, subject) {
        state.subject = subject;
    },

    updateRecipients(state, recipients) {
        state.recipients = recipients;
    },

    updateReceiveCopy(state, receive_copy) {
        state.receive_copy = receive_copy;
    },

    updateSendCopyToBillingEmails(state, send_copy_to_billing_emails) {
        state.send_copy_to_billing_emails = send_copy_to_billing_emails;
    },

    updateReminder(state, reminder) {
        state.reminder = reminder;
        applyTemplates(state);
    },

    updateTemplateSource(state, template_source) {
        state.template_source = template_source;
        applyTemplates(state);
    },
};

const actions = {
    async sendInvoice({ commit, dispatch, state }) {
        let processedMessage = state.message;
        if (processedMessage && /\{\{[^}]+\}\}/.test(processedMessage)) {
            processedMessage = replaceInvoiceVariables(
                processedMessage,
                state.invoice,
                state.billingaccount
            );
        }

        const sendparams = {
            receive_copy: state.receive_copy,
            send_copy_to_billing_emails: state.send_copy_to_billing_emails,
            recipients: state.recipients,
            subject: state.subject,
            message: processedMessage,
            reminder: state.reminder,
        };
        commit('updateSendingInvoice', true);
        commit('updateSendingInvoiceErrors', {});
        try {
            let url = `/api/customerinvoices/${state.invoice.id}/send/`;
            if (
                state.invoice.order &&
                (!state.invoice.biller ||
                    state.invoice.biller != store.getters['session/current_entity_id'])
            ) {
                url = `/api/customerorders/${state.invoice.order}/invoices/${state.invoice.id}/send/`;
            }
            const response = await axios.post(url, sendparams);
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
            return response.data;
        } catch (xhr_error) {
            const error = storeUtils.handleError(xhr_error);
            commit('updateSendingInvoiceErrors', error.details);
            throw error;
        } finally {
            commit('updateSendingInvoice', false);
        }
    },

    async fetchDefaultBillingAccount({ commit, state }) {
        const orderId = invoiceOrderId(state.invoice);
        const billingaccountId =
            state.invoice?.billed_data?.billingaccount ||
            state.invoice?.billingaccount;
        try {
            // Sub-order invoices carry the vendor's buyer billing account.
            // Agents must use the master order's billing account (notification
            // templates, reply-to). The nested endpoint already switches on
            // seller vs agent; see get_order_billingaccount.
            const url = orderId
                ? `/api/customerorders/${orderId}/billingaccount/`
                : `/api/billingaccounts/${billingaccountId}/`;
            const response = await axios.get(url);
            commit('updateBillingAccount', response.data);
            return response.data;
        } catch (xhr_error) {
            throw storeUtils.handleError(xhr_error);
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateReminder', params.reminder || false);
        commit('updateInvoice', params?.invoice);
        commit('updateBilled', params?.invoice?.billed_data);
        commit('updateSendingInvoiceErrors', {});

        const emails = params.invoice.billed_data.billing_address?.emails;
        commit('updateRecipients', emails || []);
        commit('updateReceiveCopy', false);
        commit('updateSendCopyToBillingEmails', false);
        commit('updateSendingInvoice', false);
        const orderId = invoiceOrderId(params?.invoice);
        if (!orderId && params.invoice.billingaccount_data) {
            commit('updateBillingAccount', params.invoice.billingaccount_data);
        } else {
            dispatch('fetchDefaultBillingAccount');
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
